import { FC, useMemo, useState } from "react";
import { Box, Stack, Typography, Button, Alert } from "@mui/material";
import CustomTable from "components/Table/CustomTable";
import { DomainColorSquares } from "components/Requirements";
import { TABLE_ACTIONS, REQUIREMENT } from "helpers/constants";
import { Requirement } from "types/requirement";
import { EntitySelectorWithTabs } from "modals/EntitySelector";
import { getFilteredList } from "utils/Array";
import { requirementsHaveDomainSwatches } from "utils/requirementDomains";

export type RequirementsSelectorProps = {
  organizationId?: string | null;
  allRequirements: Requirement[];
  selectedRequirements: Requirement[];
  onAddRequirement: (req: Requirement) => void;
  onRemoveRequirement: (requirementId: string) => void;
  onNext: () => void;
  errorMessage?: string | null;
};

export const RequirementsSelector: FC<RequirementsSelectorProps> = ({
  organizationId,
  allRequirements,
  selectedRequirements,
  onAddRequirement,
  onRemoveRequirement,
  onNext,
  errorMessage,
}) => {
  const [openSelector, setOpenSelector] = useState(false);

  const showDomainsColumn = useMemo(
    () => requirementsHaveDomainSwatches(selectedRequirements),
    [selectedRequirements],
  );

  const columns = useMemo(
    () => [
      {
        id: "name",
        label: "Name",
        value: "name",
      },
      {
        id: "description",
        label: "Requirement Description",
        value: (row: any) =>
          row.description?.length > 128
            ? row.description.slice(0, 128)
            : row.description,
      },
      ...(showDomainsColumn
        ? [
            {
              id: "domains",
              label: "Domains",
              value: (row: any) => <DomainColorSquares requirement={row} />,
            },
          ]
        : []),
      { id: "actions", value: "" },
    ],
    [organizationId, showDomainsColumn],
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Select requirements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use the selector to add items. They will appear in the table below.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => setOpenSelector(true)}>
          Select Requirement
        </Button>
      </Stack>

      <CustomTable
        entities={selectedRequirements}
        handleActionClick={(action, id) => {
          if (action === TABLE_ACTIONS.DELETE) onRemoveRequirement(id);
        }}
        fieldToFilter={(row: any, filterText: string) =>
          row.name.toLowerCase().includes(filterText.toLowerCase()) ||
          row.description?.toLowerCase().includes(filterText.toLowerCase())
        }
        columns={columns as any}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"requirement_id"}
        enableCardViewInSmallDevices={true}
        renderCardContent={(row: Requirement) => {
          const description =
            row.description && row.description.length > 128
              ? row.description.slice(0, 128)
              : row.description;
          return (
            <Stack spacing={1} pr={5}>
              <Typography variant="subtitle2">{row.name}</Typography>
              {description && (
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              )}
              {showDomainsColumn && (
                <DomainColorSquares requirement={row} showLabels />
              )}
            </Stack>
          );
        }}
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={onNext}>
          Next
        </Button>
      </Stack>

      <EntitySelectorWithTabs
        open={openSelector}
        onClose={() => setOpenSelector(false)}
        title={"Select Requirements"}
        listEntities={getFilteredList(
          allRequirements,
          selectedRequirements,
          "requirement_id",
        )}
        filterCriteria={(search: string, data: any) =>
          data.name.toLowerCase().includes(search.toLowerCase())
        }
        onSelect={(selected: any) => {
          const toAdd = Array.isArray(selected) ? selected : [selected];
          toAdd.forEach((req: Requirement) => onAddRequirement(req));
        }}
        displayField="name"
        keyField="requirement_id"
        multiSelect
        tabs={[
          {
            label: "EPA",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_EPA,
          },
          {
            label: "Competency",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
          },
        ]}
      />
    </Box>
  );
};
